import React from 'react';
import {
  Calendar,
  Edit2,
  Trash2,
  Eye,
  ChevronRight,
  Clock,
  Play,
  Download,
  CheckCircle,
} from 'lucide-react';
import { CourseMiniatureCanvas } from '../CourseMiniatureCanvas';
import { ElevationSparkline } from '../ElevationSparkline';
import { ProgressBar } from '../ProgressBar';
import { formatEta } from '../../utils/formatters';

interface TrackCardProps {
  track: any;
  isSelected: boolean;
  hoveredTrackId: string | null;
  setHoveredTrackId: (id: string | null) => void;
  toggleSelect: (id: string, e: React.MouseEvent) => void;
  editingTrackId: string | null;
  editingName: string;
  setEditingName: (name: string) => void;
  startEditing: (trackId: string, currentName: string, e: React.MouseEvent) => void;
  cancelEditing: () => void;
  saveName: (trackId: string) => Promise<void>;
  formatDate: (isoString: string) => string;
  formatDistance: (meters: number) => string;
  formatDuration: (seconds: number) => string;
  formatSpeedOrPace: (speedMps: number, activityType: string) => string;
  getActivityEmoji: (type: string) => string;
  completedJobs: any[];
  activeJobs: any[];
  setPlayingVideoUrl: (url: string | null) => void;
  getFullVideoUrl: (path?: string | null) => string;
  handleDeleteRenderClick: (jobId: string) => void;
  handleDeleteClick: (trackId: string, e: React.MouseEvent) => void;
  navigate: (path: string) => void;
}

export const TrackCard: React.FC<TrackCardProps> = ({
  track,
  isSelected,
  hoveredTrackId,
  setHoveredTrackId,
  toggleSelect,
  editingTrackId,
  editingName,
  setEditingName,
  startEditing,
  cancelEditing,
  saveName,
  formatDate,
  formatDistance,
  formatDuration,
  formatSpeedOrPace,
  getActivityEmoji,
  completedJobs,
  activeJobs,
  setPlayingVideoUrl,
  getFullVideoUrl,
  handleDeleteRenderClick,
  handleDeleteClick,
  navigate,
}) => {
  const getProviderBadge = (t: any) => {
    if (!t.provider) {
      return (
        <span
          style={{
            fontSize: '11px',
            color: 'var(--text-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
          title="Manual File Upload"
        >
          📁 GPX
        </span>
      );
    }

    let providerLink = '';
    if (t.provider === 'strava' && t.providerActivityId) {
      providerLink = `https://www.strava.com/activities/${t.providerActivityId}`;
    } else if (t.provider === 'garmin' && t.providerActivityId) {
      providerLink = `https://connect.garmin.com/modern/activity/${t.providerActivityId}`;
    }

    const badgeContent = (
      <span
        style={{
          fontSize: '11px',
          fontWeight: 700,
          color: t.provider === 'strava' ? '#fc5200' : '#45b8e2',
          background: t.provider === 'strava' ? 'rgba(252, 82, 0, 0.1)' : 'rgba(69, 184, 226, 0.1)',
          border: `1px solid ${t.provider === 'strava' ? 'rgba(252, 82, 0, 0.2)' : 'rgba(69, 184, 226, 0.2)'}`,
          padding: '2px 6px',
          borderRadius: '6px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: providerLink ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
        }}
      >
        {t.provider === 'strava' ? '🟠 Strava' : '🔵 Garmin'}
      </span>
    );

    if (providerLink) {
      return (
        <a
          href={providerLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ textDecoration: 'none' }}
          title={`View activity on ${t.provider === 'strava' ? 'Strava' : 'GarminConnect'}`}
        >
          {badgeContent}
        </a>
      );
    }

    return badgeContent;
  };
  const trackRenders = completedJobs.filter((j) => j.trackId === track.id);
  const trackActiveRenders = activeJobs.filter((j) => j.trackId === track.id);
  const isHovered = hoveredTrackId === track.id;

  return (
    <div
      onMouseEnter={() => setHoveredTrackId(track.id)}
      onMouseLeave={() => setHoveredTrackId(null)}
      className="glass-panel"
      style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '460px',
        background: isHovered ? 'rgba(255, 255, 255, 0.05)' : 'rgba(17, 24, 39, 0.45)',
        border: isHovered
          ? '1px solid var(--accent-primary)'
          : '1px solid rgba(255, 255, 255, 0.05)',
        transform: isHovered ? 'translateY(-4px)' : 'none',
        boxShadow: isHovered ? '0 12px 30px rgba(0,0,0,0.5)' : 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
      }}
    >
      {/* Checkbox for Selection */}
      <div
        onClick={(e) => toggleSelect(track.id, e)}
        style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          border: isSelected ? 'none' : '2px solid rgba(255,255,255,0.3)',
          background: isSelected ? 'var(--accent-gradient)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 10,
          transition: 'all 0.2s ease',
          opacity: isSelected || isHovered ? 1 : 0,
        }}
      >
        {isSelected && <CheckCircle size={14} style={{ color: '#fff' }} />}
      </div>

      <div>
        {/* Card Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '14px',
            paddingLeft: isSelected || isHovered ? '22px' : '0',
            transition: 'padding 0.2s ease',
          }}
        >
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', marginRight: '10px', flex: 1 }}>
            {editingTrackId === track.id ? (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveName(track.id);
                    else if (e.key === 'Escape') cancelEditing();
                  }}
                  onBlur={(e) => {
                    const target = e.relatedTarget as HTMLElement;
                    if (target && (target.innerText === 'Save' || target.innerText === 'Cancel')) {
                      return;
                    }
                    setTimeout(() => {
                      cancelEditing();
                    }, 150);
                  }}
                  autoFocus
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid var(--accent-primary)',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '4px 8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    width: '120px',
                    flex: 1,
                    outline: 'none',
                  }}
                />
                <button
                  onClick={() => saveName(track.id)}
                  className="btn btn-primary"
                  style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px' }}
                >
                  Save
                </button>
                <button
                  onClick={cancelEditing}
                  className="btn btn-secondary"
                  style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px' }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#fff',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    margin: 0,
                    cursor: 'pointer',
                  }}
                  title="Click to rename activity"
                  onClick={(e) => startEditing(track.id, track.name, e)}
                >
                  {track.name}
                </h3>
                <button
                  onClick={(e) => startEditing(track.id, track.name, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.6,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                  title="Rename activity"
                >
                  <Edit2 size={12} />
                </button>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
              }}
            >
              <Calendar size={12} />
              <span>{formatDate(track.startTime)}</span>
            </div>
          </div>

          {/* Activity Type Badge & Provider Badge */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'rgba(255,255,255,0.03)',
                padding: '4px 10px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.05)',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              <span>{getActivityEmoji(track.activityType)}</span>
              <span>{track.activityType}</span>
            </div>
            {getProviderBadge(track)}
          </div>
        </div>

        {/* Course Miniature Section */}
        <div
          style={{ marginBottom: '16px', position: 'relative', cursor: 'pointer' }}
          onClick={() => navigate(`/preview/${track.id}`)}
        >
          <CourseMiniatureCanvas
            coordinates={track.simplifiedCoordinates}
            activityType={track.activityType}
            isHovered={isHovered}
          />
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(11, 15, 25, 0.75)',
              backdropFilter: 'blur(4px)',
              borderRadius: '6px',
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <span>View Route Map</span>
          </div>
        </div>

        {/* Elevation Sparkline Section */}
        <div style={{ marginBottom: '16px' }}>
          <span
            style={{
              fontSize: '10px',
              color: 'var(--text-muted)',
              display: 'block',
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Elevation Profile
          </span>
          <ElevationSparkline
            coordinates={track.simplifiedCoordinates}
            activityType={track.activityType}
          />
        </div>

        {/* Stats Table Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            background: 'rgba(0, 0, 0, 0.15)',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.02)',
            marginBottom: '16px',
          }}
        >
          <div>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                display: 'block',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Distance
            </span>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              {formatDistance(track.totalDistance)}
            </strong>
          </div>
          <div>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                display: 'block',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Elevation Gain
            </span>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              +{Math.round(track.totalElevationGain)}m
            </strong>
          </div>
          <div>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                display: 'block',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Duration
            </span>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              {formatDuration(track.totalDuration)}
            </strong>
          </div>
          <div>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                display: 'block',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Avg Pace/Speed
            </span>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              {formatSpeedOrPace(track.avgSpeed, track.activityType)}
            </strong>
          </div>
        </div>
      </div>

      {/* Renders Section & Actions */}
      <div>
        {/* Active & Completed Renders List */}
        <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              display: 'block',
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Rendered Videos
          </span>

          {trackRenders.length === 0 && trackActiveRenders.length === 0 ? (
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              No videos rendered yet. Go to Preview to render.
            </span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {/* Completed Renders */}
              {trackRenders.map((job) => (
                <div
                  key={job.jobId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    background: 'rgba(16,185,129,0.08)',
                    border: '1px solid rgba(16,185,129,0.2)',
                    borderRadius: '6px',
                    padding: '2px 6px',
                  }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#10b981' }}>
                    {job.resolution}
                  </span>
                  <button
                    onClick={() => setPlayingVideoUrl(job.videoUrl || null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#10b981',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="Play video"
                  >
                    <Play size={11} style={{ fill: '#10b981' }} />
                  </button>
                  <a
                    href={getFullVideoUrl(job.videoUrl)}
                    download
                    style={{ color: '#10b981', padding: '2px', display: 'flex', alignItems: 'center' }}
                    title="Download video"
                  >
                    <Download size={11} />
                  </a>
                  <button
                    onClick={() => handleDeleteRenderClick(job.jobId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="Delete video"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}

              {/* Active rendering status */}
              {trackActiveRenders.map((job) => (
                <div
                  key={job.jobId}
                  onClick={() => navigate(`/status/${job.jobId}`)}
                  className="glass-panel-hover"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    width: '100%',
                    background: 'rgba(245,158,11,0.06)',
                    border: '1px solid rgba(245,158,11,0.18)',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '11px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        color: '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Clock size={10} style={{ animation: 'pulse 1.8s infinite' }} />
                      Rendering {job.resolution}...
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {job.etaSeconds !== undefined && job.etaSeconds > 0 && (
                        <span style={{ fontSize: '10px', opacity: 0.8, color: '#f59e0b' }}>
                          ETA: {formatEta(job.etaSeconds)}
                        </span>
                      )}
                      <span style={{ fontWeight: 700, color: '#f59e0b' }}>{job.progress}%</span>
                    </div>
                  </div>
                  <ProgressBar progress={job.progress} height={4} marginTop={6} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card Footer Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            paddingTop: '14px',
            marginTop: '10px',
          }}
        >
          <button
            onClick={(e) => handleDeleteClick(track.id, e)}
            className="btn btn-danger"
            style={{
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Delete Track & Renders"
          >
            <Trash2 size={15} />
          </button>

          <button
            onClick={() => navigate(`/preview/${track.id}`)}
            className="btn btn-secondary"
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Eye size={15} />
            <span>Customize & Render</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
